import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Query } from '../op-types';

/**
 * Admin growth + revenue analytics (registrations, subscriptions/MRR). All GET → queries.
 * Keys are the invalidation SSOT the admin hooks reuse (`stoxly.queries.adminAnalytics.*`).
 */
export function createAdminAnalyticsQueries(client: StoxlyClient) {
  return createQueryKeys('adminAnalytics', {
    // ['adminAnalytics', 'registrationOverview', { query }]
    registrationOverview: (query?: Query<'adminAnalyticsGetRegistrationOverview'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.adminAnalyticsGetRegistrationOverview({ queries: query, signal }),
    }),

    // ['adminAnalytics', 'registrationTrend', { query }]
    registrationTrend: (query?: Query<'adminAnalyticsGetRegistrationTrend'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.adminAnalyticsGetRegistrationTrend({ queries: query, signal }),
    }),

    // ['adminAnalytics', 'subscriptionOverview', { query }]
    subscriptionOverview: (query?: Query<'adminAnalyticsGetSubscriptionOverview'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.adminAnalyticsGetSubscriptionOverview({ queries: query, signal }),
    }),

    // ['adminAnalytics', 'subscriptionTrend', { query }]
    subscriptionTrend: (query?: Query<'adminAnalyticsGetSubscriptionTrend'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.adminAnalyticsGetSubscriptionTrend({ queries: query, signal }),
    }),

    // ['adminAnalytics', 'subscriptionPlanMix'] — no params
    subscriptionPlanMix: {
      queryKey: null,
      queryFn: ({ signal }) => client.adminAnalyticsGetSubscriptionPlanMix({ signal }),
    },

    // ['adminAnalytics', 'earlyBird'] — the write-once cutoff + how many subscribers qualify now
    earlyBird: {
      queryKey: null,
      queryFn: ({ signal }) => client.adminAnalyticsGetEarlyBird({ signal }),
    },

    // ['adminAnalytics', 'earlyBirdPortalConfigurations'] — the account's portal configurations
    earlyBirdPortalConfigurations: {
      queryKey: null,
      queryFn: ({ signal }) => client.adminAnalyticsListEarlyBirdPortalConfigurations({ signal }),
    },

    // ['adminAnalytics', 'subscriptionList', { query }]
    subscriptionList: (query?: Query<'adminAnalyticsGetSubscriptionList'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.adminAnalyticsGetSubscriptionList({ queries: query, signal }),
    }),
  });
}
